/**
 * 牛只卡片（网格 / 列表两种模式）
 * 封面图缺失时展示品牌默认牛只图（public/images/cattle-default.jpg）
 */
import { Link } from 'react-router-dom';
import type { Cattle } from '@/types';
import { GENDER_MAP, SALE_STATUS, TRADE_MODE, cosUrl, imgFallback } from '@/utils/constants';
import { formatDistance, formatMoney, formatWeight } from '@/utils/format';
import StatusTag from './StatusTag';

const DEFAULT_COVER = cosUrl('/images/cattle-default.jpg');
/** 本地默认图路径（COS 不可达时回退） */
const DEFAULT_COVER_LOCAL = '/images/cattle-default.jpg';

interface CattleCardProps {
  cattle: Cattle;
  mode?: 'grid' | 'list';
}

function Cover({ cattle }: { cattle: Cattle }) {
  const image = cattle.images?.[0] ?? cattle.face_image_url ?? DEFAULT_COVER;
  return (
    <img
      src={image}
      alt={cattle.breed ?? '牛只'}
      loading="lazy"
      data-local={DEFAULT_COVER_LOCAL}
      onError={imgFallback}
    />
  );
}

function Stars({ score }: { score?: number }) {
  const value = score ?? 0;
  return (
    <span className="stars" title={`溯源完整度 ${value}/5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={i <= value ? '' : 'off'}>★</span>
      ))}
    </span>
  );
}

export default function CattleCard({ cattle, mode = 'grid' }: CattleCardProps) {
  const tradeMode = TRADE_MODE[cattle.trade_mode ?? 1] ?? TRADE_MODE[1];
  const saleStatus = SALE_STATUS[cattle.sale_status ?? 0];

  if (mode === 'list') {
    return (
      <Link className="cattle-card cattle-card--list" to={`/cattle/${cattle.id}`}>
        <div className="cattle-card__cover">
          <Cover cattle={cattle} />
          <span className={`cattle-card__sale status-tag status-tag--${saleStatus.tone}`}>
            {saleStatus.label}
          </span>
        </div>
        <div className="cattle-card__body">
          <div className="cattle-card__head">
            <h3 className="cattle-card__title">{cattle.breed ?? '牛只'} · {cattle.ear_tag_no}</h3>
            <StatusTag type="trade" status={cattle.trade_mode ?? 1} />
          </div>
          <div className="cattle-card__meta">
            <span>{GENDER_MAP[cattle.gender ?? 0]}</span>
            <span>{formatWeight(cattle.weight)}</span>
            <span>{cattle.age_days ?? '-'} 日龄</span>
            <span>{cattle.farm_name ?? '-'}</span>
          </div>
          <div className="cattle-card__foot">
            <span className="cattle-card__price">{formatMoney(cattle.listing_price)}<em>/头</em></span>
            <span className="cattle-card__dist">{formatDistance(cattle.distance_km)}</span>
            <Stars score={cattle.trace_score} />
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link className="cattle-card" to={`/cattle/${cattle.id}`}>
      <div className="cattle-card__cover">
        <Cover cattle={cattle} />
        <span className={`cattle-card__sale status-tag status-tag--${saleStatus.tone}`}>
          {saleStatus.label}
        </span>
        <span className="cattle-card__trade status-tag status-tag--outline">{tradeMode.label}</span>
      </div>
      <div className="cattle-card__body">
        <h3 className="cattle-card__title">{cattle.breed ?? '牛只'} · {cattle.ear_tag_no}</h3>
        <div className="cattle-card__meta">
          <span>{GENDER_MAP[cattle.gender ?? 0]}</span>
          <span>{formatWeight(cattle.weight)}</span>
          <span>{cattle.age_days ?? '-'}日</span>
          <span className="cattle-card__farm">{cattle.farm_name ?? '-'}</span>
        </div>
        <div className="cattle-card__foot">
          <span className="cattle-card__price">
            {formatMoney(cattle.listing_price)}<em>/头</em>
          </span>
          <span className="cattle-card__dist">{formatDistance(cattle.distance_km)}</span>
        </div>
        <div className="cattle-card__extra">
          <Stars score={cattle.trace_score} />
          <span className="cattle-card__qty">可售 {cattle.available_qty ?? 1} 头</span>
        </div>
      </div>
    </Link>
  );
}
